import { useMemo } from 'react';
import { supabaseCoreClient } from '../supabase.ts';
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { encode } from "@supabase-cache-helpers/postgrest-react-query";
import { queryClient } from '../constants.js';
import { commonQueryProps } from './cache.js';
import type { GridPresetInfo, GridPresetMetadata } from '../../types/metadata.js';
import type { CachedQueryResult } from '../../types/supabase.js';
import { persistedRelations, useMetadataBootReady } from '../persistentMetadataCache.js';

/**
 * Builds the Supabase query for fetching grid presets
 * @returns PostgrestBuilder query for preset_table_filter with all necessary columns
 */
export const buildQueryForGridPresets = () => {
  return supabaseCoreClient
    .from('preset_table_filter')
    .select('*')
    // .or(`created_by.eq.${currentUserId},is_public.eq.true`);
};

/**
 * Post-processes grid presets data by grouping by component and name
 * @param data - Raw grid preset data from database
 * @returns Processed grid preset metadata grouped by component
 */
export const postProcessGridPresets = (data: GridPresetInfo[]): GridPresetMetadata => {
  const groupedByComponent: GridPresetMetadata = {
    flattened: {},
    byId: {},
    byName: {},
    byComponentId: {},
    asList: data
  };

  data.forEach(preset => {
    const componentId = preset.ui_component_id;
    if (componentId !== null) {
      if (!groupedByComponent.byComponentId[componentId]) {
        groupedByComponent.byComponentId[componentId] = { asList: [], byName: {} };
      }
      groupedByComponent.byComponentId[componentId].asList.push(preset);
      groupedByComponent.byComponentId[componentId].byName[preset.name] = preset;
    }
    groupedByComponent.byName[preset.name] = preset;
    groupedByComponent.flattened[`${componentId ?? 'report'}_${preset.name}`] = preset;
    groupedByComponent.byId[preset.id] = preset;
  });

  return groupedByComponent;
};

// Result type for grid preset operations
interface GridPresetResult {
  data: GridPresetMetadata | null;
  error: any;
}

/**
 * Fetches grid presets data outside of React context
 * @returns Promise with processed grid preset metadata or error
 */
export const getGridPresets = async (): Promise<GridPresetResult> => {
  const query = buildQueryForGridPresets();
  let presetData: GridPresetInfo[] | null = null;
  let presetError: any = null;
  
  try {
    // TypeScript cannot infer the Supabase cache structure - see SupabaseCacheData type definition
    const cachedResult = queryClient.getQueryData(encode(query, false)) as CachedQueryResult<GridPresetInfo[]>;
    presetData = cachedResult?.data ?? null;
  } catch {
    presetData = null;
  }
  
  if (!presetData) {
    const { data, error } = await query;
    presetData = data;
    presetError = error;
  }

  if (presetError) {
    return { data: null, error: presetError };
  }

  const processedData = postProcessGridPresets(presetData ?? []);
  return { data: processedData, error: null };
};

// Hook options interface
interface UseGridPresetsOptions {
  enabled?: boolean;
}

// Hook result interface
interface UseGridPresetsResult {
  data: GridPresetMetadata | null;
  error: any;
  isLoading: boolean;
}

/**
 * React hook to fetch grid presets data using React Query
 * @param options - Options including enabled flag
 * @returns Grid presets data with loading and error states
 */
export const useGridPresets = ({ enabled }: UseGridPresetsOptions): UseGridPresetsResult => {
  const bootReady = useMetadataBootReady();
  const query = buildQueryForGridPresets();

  const { data, error, isPending } = useQuery(
    query,
    {
      ...commonQueryProps,
      enabled: enabled && bootReady,
      meta: persistedRelations('core_crm.preset_table_filter')
    }
  );

  // Memoized on the query result so the processed object keeps a stable
  // identity across renders — consumers memoize off it.
  const processedData = useMemo(() => (data ? postProcessGridPresets(data) : null), [data]);

  if (isPending || error || !data) {
    return {
      data: null,
      error,
      isLoading: isPending
    };
  }

  return {
    data: processedData,
    error: null,
    isLoading: false
  };
};
